// API URL should be configured via environment variables for both dev and prod
// Dev: http://localhost:3000
// Prod: https://api.directory.modus.tools
const API_URL = import.meta.env.VITE_API_URL;

if (!API_URL) {
  throw new Error('VITE_API_URL environment variable is required');
}

import { isTokenExpirationError, storeRedirectInfo } from './auth';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');
  const headers = new Headers(options.headers);
  
  if (token) {
    // Ensure token is properly formatted
    const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    headers.set('Authorization', bearerToken);
  }
  headers.set('Content-Type', 'application/json');

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorText;
      } catch {
        errorMessage = errorText;
      }
      
      // Check for authentication errors (401 status)
      if (response.status === 401) {
        const isExpiredToken = isTokenExpirationError(errorMessage);
        
        if (isExpiredToken) {
          // Prevent redirect loop - don't redirect if already on login page
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && !currentPath.includes('/auth/')) {
            // Store current location before redirecting
            storeRedirectInfo(
              window.location.pathname + window.location.search,
              'Your session has expired. Please sign in again.'
            );
            
            // Redirect to login page
            window.location.href = '/login';
          }
        }
      }
      
      throw new ApiError(response.status, errorMessage);
    }

    // Only try to parse JSON if there's content
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      // Simple direct response handling - no success/data wrapping
      return await response.json() as T;
    }
    
    return response.text() as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    // Handle network errors or other unexpected errors
    console.error('API request failed:', error);
    throw new ApiError(0, 'Network error or server unavailable');
  }
}

export const api = {
  get: <T>(endpoint: string) => 
    fetchWithAuth<T>(endpoint),

  post: <T>(endpoint: string, data?: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: <T>(endpoint: string, data: unknown) =>
    fetchWithAuth<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string) =>
    fetchWithAuth<T>(endpoint, {
      method: 'DELETE',
    }),
};

export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
}; 